import { useState } from 'react';

import { Box, Button, Chip, Container, Pagination, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import { Link, redirect } from 'react-router';

import { get, getOr } from '@/api/client';
import { RUTAS } from '@/api/rutas';
import { comoRespuesta } from '@/lib/errores';
import { MedirVisita } from '@/components/MedirVisita';
import { TarjetaDeProducto } from '@/components/TarjetaDeProducto';
import { VistaRapida } from '@/components/VistaRapida';
import { nombreDeMarca } from '@/lib/catalogo';
import { listaDeProductos, migasDePan, SITIO } from '@/lib/datos-estructurados';
import { esElRubro, SIN_RUBRO, slugDeRubro } from '@/lib/rubros';
import { useDatosDelSitio } from '@/root';
import type { Imagen, Paginacion, Producto } from '@/tipos';

import type { Route } from './+types/rubros.$slug';

/** Cuántos productos entran en cada página del rubro. */
const POR_PAGINA = 24;

/** Cuántos otros rubros se ofrecen abajo, para seguir mirando. */
const VECINOS = 10;

interface TarjetaDeLaApi {
  name: string;
  products: number;
  image: string | null;
}

interface Respuesta {
  products: Producto[];
  pagination: Paginacion;
}

export function meta({ data }: Route.MetaArgs): Route.MetaDescriptors {
  if (!data) return [{ title: 'Rubro no encontrado | CKING' }];

  const { rubro, pagina, canonical, marcas, total, imagen } = data;

  const titulo =
    `${rubro} — Repuestos para motos | CKING` + (pagina > 1 ? ` — Página ${pagina}` : '');

  const marcasTexto = marcas.length > 0 ? ` Marcas ${marcas.map(nombreDeMarca).join(', ')}.` : '';

  return [
    { title: titulo },
    {
      name: 'description',
      content:
        `${total} repuestos para motos del rubro ${rubro.toLocaleLowerCase('es')} en el ` +
        `catálogo mayorista de CKING.${marcasTexto} Código, foto y disponibilidad de cada pieza.`,
    },
    { tagName: 'link', rel: 'canonical', href: canonical },
    { property: 'og:title', content: titulo },
    // Una pieza del rubro al compartir el link, en vez de la portada del sitio.
    ...(imagen ? [{ property: 'og:image', content: imagen }] : []),
  ];
}

/**
 * Un rubro del catálogo, con su propia dirección.
 *
 * Antes esto era `/catalogo?category=ARANDELAS`: la misma página del catálogo con otra lista
 * adentro, con el título del catálogo entero y con un canonical que mandaba a `/catalogo`. Para
 * Google eran 97 direcciones que él mismo tenía que descartar, y el sitio quedaba con una sola
 * puerta de entrada. Nadie busca "repuestos para motos" y compra: busca "arandelas para moto".
 *
 * El filtro del catálogo sigue existiendo para quien ya está adentro mirando; esta página es la
 * que se ofrece afuera.
 */
export async function loader({ params, request }: Route.LoaderArgs) {
  const pedido = new URL(request.url);
  const pagina = Number.parseInt(pedido.searchParams.get('page') ?? '1', 10) || 1;

  // `get` y no `getOr`: con la lista vacía de reserva, una API caída hacía que este rubro
  // contestara 404, y eso le dice a Google que el rubro ya no existe.
  let categories: TarjetaDeLaApi[];

  try {
    ({ categories } = await get<{ categories: TarjetaDeLaApi[] }>(
      RUTAS.catalogo.categoriasEnTarjetas,
      { request },
    ));
  } catch (fallo) {
    throw comoRespuesta(fallo);
  }

  const rubros = categories.filter(c => c.name.trim().toUpperCase() !== SIN_RUBRO);
  const rubro = rubros.find(c => esElRubro(c.name, params.slug));

  if (!rubro) throw new Response('Rubro no encontrado', { status: 404 });

  // Una sola dirección por rubro: /rubros/ARANDELAS o /rubros/arandelas son la misma página, y
  // dos direcciones para lo mismo es lo que veníamos de arreglar.
  const canonico = slugDeRubro(rubro.name);
  if (params.slug !== canonico) throw redirect(`/rubros/${canonico}`, 301);

  const catalogo = await get<Respuesta>(RUTAS.catalogo.productos, {
    request,
    query: { category: rubro.name, page: pagina, limit: POR_PAGINA },
  });

  // Una página que no existe manda a la última que sí, como en el catálogo.
  if (catalogo.pagination.total > 0 && pagina > catalogo.pagination.totalPages) {
    throw redirect(`/rubros/${canonico}?page=${catalogo.pagination.totalPages}`);
  }

  // Las fotos de esta página, en paralelo y desde el servidor: son veinticuatro consultas a una
  // API que está en la misma máquina, y el HTML sale con las fotos ya puestas.
  const fotos: Record<string, string | null> = Object.fromEntries(
    await Promise.all(
      catalogo.products.map(async producto => {
        const { images } = await getOr<{ images: Imagen[] }>(
          RUTAS.catalogo.fotosDelProducto(producto.code),
          { images: [] },
          { request },
        );

        return [producto.code, images[0]?.url ?? null] as const;
      }),
    ),
  );

  const marcas = [...new Set(catalogo.products.map(p => p.brand).filter(Boolean))] as string[];

  // Los rubros de al lado en el abecedario. Es la manera de que se pueda seguir mirando sin
  // volver al índice, y de que las 97 páginas queden enlazadas entre ellas.
  const ordenados = [...rubros].sort((a, b) => a.name.localeCompare(b.name, 'es'));
  const donde = ordenados.findIndex(c => c.name === rubro.name);
  const vecinos = [...ordenados.slice(donde + 1), ...ordenados.slice(0, donde)]
    .slice(0, VECINOS)
    .map(c => ({ nombre: c.name, slug: slugDeRubro(c.name) }));

  return {
    rubro: rubro.name,
    total: rubro.products,
    imagen: rubro.image,
    productos: catalogo.products,
    pagination: catalogo.pagination,
    fotos,
    marcas,
    vecinos,
    pagina,
    canonical: `${SITIO}/rubros/${canonico}${pagina > 1 ? `?page=${pagina}` : ''}`,
  };
}

export default function Rubro({ loaderData }: Route.ComponentProps) {
  const { rubro, total, productos, pagination, fotos, marcas, vecinos, pagina } = loaderData;
  const { sesion } = useDatosDelSitio();

  const [mirando, setMirando] = useState<Producto | null>(null);
  const conPrecios = sesion !== null;

  return (
    <Box sx={{ py: 4, bgcolor: '#121212', minHeight: '100vh' }}>
      <MedirVisita path={`/rubros/${slugDeRubro(rubro)}`} />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify([
            migasDePan([
              { nombre: 'Rubros', ruta: '/rubros' },
              { nombre: rubro, ruta: `/rubros/${slugDeRubro(rubro)}` },
            ]),
            listaDeProductos(productos, rubro),
          ]),
        }}
      />

      <Container maxWidth="xl">
        <Box
          component="nav"
          aria-label="Breadcrumb"
          sx={{ mb: 1, fontSize: 14, color: 'text.secondary' }}
        >
          <Link to="/" style={{ color: 'inherit' }}>
            Inicio
          </Link>
          <Box component="span" aria-hidden="true" sx={{ mx: 1 }}>
            ›
          </Box>
          <Link to="/rubros" style={{ color: 'inherit' }}>
            Rubros
          </Link>
          <Box component="span" aria-hidden="true" sx={{ mx: 1 }}>
            ›
          </Box>
          <Box component="span" aria-current="page" sx={{ color: 'text.primary', fontWeight: 600 }}>
            {rubro}
          </Box>
        </Box>

        <Typography variant="h1" component="h1" sx={{ mb: 1 }}>
          {rubro}
        </Typography>

        <Typography sx={{ color: 'text.secondary', mb: 2, maxWidth: 860 }}>
          {total} {total === 1 ? 'repuesto' : 'repuestos'} para motos del rubro{' '}
          {rubro.toLocaleLowerCase('es')} que CKING importa y distribuye al por mayor en Argentina.
          Cada pieza con su código, su foto y si hay stock.
          {conPrecios
            ? ''
            : ' Los precios mayoristas se ven iniciando sesión con tu cuenta de cliente.'}
        </Typography>

        {marcas.length > 0 && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
            {marcas.map(marca => (
              <Chip
                key={marca}
                component={Link}
                to={`/catalogo?category=${encodeURIComponent(rubro)}&brand=${encodeURIComponent(marca)}`}
                clickable
                size="small"
                variant="outlined"
                label={nombreDeMarca(marca)}
              />
            ))}

            <Chip
              component={Link}
              to={`/catalogo?category=${encodeURIComponent(rubro)}`}
              clickable
              size="small"
              color="primary"
              variant="outlined"
              label="Buscar dentro del rubro"
            />
          </Box>
        )}

        {productos.length === 0 ? (
          <Typography role="status" sx={{ color: 'text.secondary', py: 6 }}>
            Este rubro quedó sin productos en la lista de precios vigente.
          </Typography>
        ) : (
          <Grid container spacing={{ xs: 2, sm: 3 }}>
            {productos.map(producto => (
              <Grid key={producto.code} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                <TarjetaDeProducto
                  producto={producto}
                  foto={fotos[producto.code] ?? null}
                  conPrecios={conPrecios}
                  onVistaRapida={setMirando}
                />
              </Grid>
            ))}
          </Grid>
        )}

        {pagination.totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
            <Pagination
              count={pagination.totalPages}
              page={pagina}
              color="primary"
              size="large"
              // Links de verdad y no botones: así cada página del rubro es una dirección que un
              // buscador puede seguir, y no algo que sólo pasa si hay JavaScript.
              renderItem={item => (
                <PaginaComoLink rubro={rubro} pagina={item.page} actual={pagina} item={item} />
              )}
            />
          </Box>
        )}

        <Box sx={{ mt: 8 }}>
          <Typography variant="h5" component="h2" sx={{ mb: 2, fontWeight: 600 }}>
            Otros rubros
          </Typography>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {vecinos.map(vecino => (
              <Chip
                key={vecino.slug}
                component={Link}
                to={`/rubros/${vecino.slug}`}
                clickable
                variant="outlined"
                label={vecino.nombre}
              />
            ))}

            <Chip
              component={Link}
              to="/rubros"
              clickable
              color="primary"
              variant="outlined"
              label="Ver los 97 rubros"
            />
          </Box>
        </Box>
      </Container>

      <VistaRapida
        producto={mirando}
        foto={mirando ? (fotos[mirando.code] ?? null) : null}
        conPrecios={conPrecios}
        onCerrar={() => setMirando(null)}
      />
    </Box>
  );
}

/**
 * Un número de la paginación, como link.
 *
 * MUI dibuja botones, y un botón no lo sigue nadie que no ejecute JavaScript. Las páginas 2 y 3
 * de un rubro tienen productos que no están en la 1: si no son direcciones, esos productos
 * dependen de que alguien llegue a ellos por otro lado.
 */
function PaginaComoLink({
  rubro,
  pagina,
  actual,
  item,
}: {
  rubro: string;
  pagina: number | null;
  actual: number;
  item: { type: string; selected: boolean; disabled: boolean };
}) {
  const destino =
    pagina === null || pagina === 1
      ? `/rubros/${slugDeRubro(rubro)}`
      : `/rubros/${slugDeRubro(rubro)}?page=${pagina}`;

  if (item.type === 'start-ellipsis' || item.type === 'end-ellipsis') {
    return <Box sx={{ px: 1, color: 'text.secondary' }}>…</Box>;
  }

  const texto =
    item.type === 'previous' ? '‹' : item.type === 'next' ? '›' : String(pagina ?? actual);

  return (
    <Button
      component={Link}
      to={destino}
      disabled={item.disabled}
      size="small"
      variant={item.selected ? 'contained' : 'text'}
      color="primary"
      aria-label={item.type === 'page' ? `Ir a la página ${pagina}` : undefined}
      aria-current={item.selected ? 'page' : undefined}
      sx={{ minWidth: 40, color: item.selected ? undefined : 'white' }}
    >
      {texto}
    </Button>
  );
}
